/**
 * @module urls
 */
const axios = require('axios');

/**
 * @typedef {object} UrlStructure
 * @property {string} collection - The data collection value.
 * @property {string} item - The data item value.
 * @property {string} subCollection - The data sub-collection value.
 */

/**
 * Convenience function to add the specified params to the url.
 *
 * @param {string} url - The url value to which to add the specified params.
 * @param {string} params - The params to add to the url.
 *
 * @returns {string} The url with params added/appended as query string.
 */
function addParamsToUrl(url, params) {
  if (!params) {
    return url;
  }
  let normalizedParams = params;
  if (normalizedParams.startsWith('?')) {
    normalizedParams = normalizedParams.substring(1);
  }
  return url && url.indexOf('?') > -1
    ? `${url}&${normalizedParams}`
    : `${url}?${normalizedParams}`;
}

/**
 * Convenience function to get and return the fingerprint for the specified url.
 *
 * @param {string} url - The url value to check for fingerprint value.
 *
 * @returns {boolean|string} The fingerprint value, or `false` if no url provided.
 */
async function getFingerprint(url) {
  if (!url || url === 'undefined') {
    return false;
  }
  let fingerprint = '';
  await axios
    .get(addParamsToUrl(url, 'w=20'))
    /* istanbul ignore next */
    .then(
      /* istanbul ignore next */ (res) => {
        if (res.status === 200 && !!url) {
          fingerprint = res.data.hashCode();
        }
      },
    )
    /* istanbul ignore next */
    .catch(() => {
      // No need to handle; blank string returned is acceptable.
    });
  return fingerprint;
}

/**
 * Convenience function to add imgixFP fingerprint to the specified url.
 *
 * @param {string} url - The URL value.
 *
 * @returns {string} The URL with fingerprint added as params to the URL.
 */
async function addFingerprintToUrl(url) {
  let fingerprint = await getFingerprint(url);
  if (fingerprint) {
    fingerprint = `imgixFP=${fingerprint}`;
  }
  return addParamsToUrl(url, fingerprint);
}

/**
 * Convenience function to replace characters in the url.
 *
 * Note: The characters replaced are the HTML representation of the following:
 * Single quote followed by a double quote, single quote, opening parenthesis,
 * and closing parenthesis.
 *
 * @param {string} url - The url value.
 *
 * @returns {string} - The url with the characters replaced. If the url is falsy 'not-set' will be returned.
 */
function escapeUrl(url) {
  if (url) {
    return url
      .replace(/(&quot;|'")/g, '')
      .replace(/'/g, '%27')
      .replace(/\(/g, '%28')
      .replace(/\)/g, '%29');
  }
  return 'not-set';
}

/**
 * Convenience function to convert the specified text string to slug value, converting spaces to hyphens.
 *
 * @param {string} text - The text value to convert to slug-friendly value.
 *
 * @returns {string} The converted string as a valid slug.
 */
function stringToSlug(text) {
  return text ? text.replace(/\s+/g, '-').toLowerCase() : '';
}

/**
 * Convenience function to generate url structure from the specified data.
 *
 * @param {object} data - The data object used to determine and generate url structure.
 *
 * @returns {UrlStructure} The url structure object.
 */
function getUrlStructureByData(data) {
  const structure = { collection: '', item: '', subCollection: '' };
  structure.item = data.media_item_type ? data.slug : '';
  const directCollection =
    data && data.mediaCollection && data.mediaCollection[0]
      ? data.mediaCollection[0]
      : data;
  if (
    [
      'konnect',
      'crosstown',
      'early-childhood',
      'earlychildhood',
      'leadershippodcast',
      'stories',
    ].includes(directCollection.slug)
  ) {
    structure.collection = directCollection.slug;
  } else if (
    ['life_groups', 'loop', 'switch', 'weekend_message', 'worship'].includes(
      directCollection.slug,
    )
  ) {
    structure.subCollection = directCollection.slug;
    structure.collection =
      directCollection &&
      directCollection.media_collection_type &&
      directCollection.media_collection_type.field
        ? directCollection.media_collection_type.field
        : '';
  } else if (
    directCollection &&
    directCollection.media_collection_type &&
    directCollection.media_collection_type.field !== directCollection.slug
  ) {
    structure.collection = directCollection.media_collection_type.field;
    structure.subCollection = directCollection.slug;
  } else {
    structure.collection = directCollection.slug;
  }
  const corrections = {
    earlychildhood: 'early-childhood',
    life_groups: 'lifegroups',
    weekend_message: 'messages',
  };
  Object.entries(structure).forEach(([k, v]) => {
    if (Object.keys(corrections).includes(v)) {
      structure[k] = corrections[v];
    }
  });

  return structure;
}

/**
 * Convenience function to get the url structure from the given media path.
 *
 * @param {string} path - The path of the media element.
 *
 * @returns {object} - The object containing the collection, subCollection and item name of the media element.
 */
function getUrlStructureByPath(path) {
  let structure = { collection: '', item: '', subCollection: '' };

  if (!path) {
    return structure;
  }

  const [, type = '', subCollection = '', item = ''] = path.split('/');
  structure = { collection: type, item, subCollection };

  return structure;
}

module.exports = {
  addFingerprintToUrl,
  addParamsToUrl,
  escapeUrl,
  getFingerprint,
  getUrlStructureByData,
  getUrlStructureByPath,
  stringToSlug,
};
